import { Monitor } from "lucide-react";
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { AccountApi } from "@hostdock/sdk";
import type { SessionRecord } from "@hostdock/types";
import { Badge } from "../badge";
import { Button } from "../button";
import { ConfirmDialog } from "../dialog";
import { describeUserAgent, formatDateTime, formatRelative } from "../format";
import { DataTable } from "../table";
import { toast } from "../toast";

export function SessionsPanel<TSession>({ api, onSignedOut }: { api: AccountApi<TSession>; onSignedOut(): void }) {
  const qc = useQueryClient();
  const key = ["account", "sessions"];
  const sessions = useQuery({ queryKey: key, queryFn: () => api.sessions() });
  const [confirmOthers, setConfirmOthers] = useState(false);
  const revoke = useMutation({
    mutationFn: (s: SessionRecord) => api.revokeSession(s.id).then(() => s),
    onSuccess: (s) => {
      if (s.current) return onSignedOut();
      toast.success("Session signed out");
      void qc.invalidateQueries({ queryKey: key });
    },
    onError: (e) => toast.error(e.message),
  });
  const revokeOthers = useMutation({ meta: { inlineError: true },
    mutationFn: () => api.revokeOtherSessions(),
    onSuccess: (r) => {
      toast.success(`Signed out ${r.revoked} other session${r.revoked === 1 ? "" : "s"}`);
      setConfirmOthers(false);
      void qc.invalidateQueries({ queryKey: key });
    },
  });
  const others = sessions.data?.items.filter((s) => !s.current).length ?? 0;

  return (
    <div>
      <DataTable
        rows={sessions.data?.items}
        loading={sessions.isPending}
        error={sessions.error}
        onRetry={() => void sessions.refetch()}
        rowKey={(s) => s.id}
        skeletonRows={2}
        columns={[
          {
            key: "device",
            header: "Device",
            cell: (s) => (
              <div className="flex items-center gap-2">
                <Monitor size={15} className="text-muted" />
                <span>{describeUserAgent(s.userAgent)}</span>
                {s.current && <Badge tone="accent">This device</Badge>}
              </div>
            ),
          },
          { key: "ip", header: "IP address", cell: (s) => <span className="font-mono text-[12.5px]">{s.ip ?? "—"}</span>, hideOnMobile: true },
          { key: "seen", header: "Last active", cell: (s) => <span title={formatDateTime(s.lastSeenAt)}>{formatRelative(s.lastSeenAt)}</span> },
          { key: "created", header: "Signed in", cell: (s) => formatDateTime(s.createdAt), hideOnMobile: true },
          {
            key: "actions",
            header: "",
            className: "text-right",
            cell: (s) => (
              <Button size="sm" variant="ghost" loading={revoke.isPending && revoke.variables?.id === s.id} onClick={() => revoke.mutate(s)}>
                {s.current ? "Sign out" : "Revoke"}
              </Button>
            ),
          },
        ]}
      />
      {others > 0 && (
        <div className="flex justify-end border-t border-line px-4 py-3">
          <Button size="sm" variant="danger-ghost" onClick={() => setConfirmOthers(true)}>
            Sign out all other sessions
          </Button>
        </div>
      )}
      <ConfirmDialog
        open={confirmOthers}
        onOpenChange={setConfirmOthers}
        title="Sign out other sessions?"
        description={`${others} other session${others === 1 ? "" : "s"} will be signed out immediately.`}
        confirmLabel="Sign out others"
        loading={revokeOthers.isPending}
        error={revokeOthers.error}
        onConfirm={() => revokeOthers.mutate()}
      />
    </div>
  );
}
